import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { MethodPill, PenIcon, CopyIcon, TrashIcon } from "@graphiql/react";
import { getDocumentMethod } from "../operation-method.js";
import SvgUpload from "../icons/upload.js";
import SvgCheck from "../icons/check.js";
const CollectionItemRow = ({
  item,
  collectionId,
  index,
  readOnly = false,
  isGrabbed,
  onGrabToggle,
  onGrabMove,
  onGrabCancel,
  onOpen,
  onShare,
  onAnnounce,
  onDelete,
  onMove,
  onRenameItem
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [confirmToken, setConfirmToken] = useState(0);
  const editFormRef = useRef(null);
  const [editName, setEditName] = useState(item.name);
  const [editDescription, setEditDescription] = useState(
    item.description ?? ""
  );
  const method = getDocumentMethod(item.query);
  const startEdit = (e) => {
    e.stopPropagation();
    setEditName(item.name);
    setEditDescription(item.description ?? "");
    setIsEditing(true);
  };
  useEffect(() => {
    if (!confirmed) {
      return;
    }
    const timer = setTimeout(() => setConfirmed(null), 1500);
    return () => clearTimeout(timer);
  }, [confirmed, confirmToken]);
  const runAction = async (kind, write, message, failureMessage) => {
    try {
      await write();
      onAnnounce(message);
      setConfirmed(kind);
      setConfirmToken((t) => t + 1);
    } catch {
      onAnnounce(failureMessage);
    }
  };
  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(collectionId, item.id);
  };
  const commitEdit = () => {
    const name = editName.trim() || item.name;
    onRenameItem(collectionId, item.id, {
      name,
      description: editDescription
    });
    setIsEditing(false);
  };
  const handleBlur = (e) => {
    var _a;
    if ((_a = editFormRef.current) == null ? void 0 : _a.contains(e.relatedTarget)) {
      return;
    }
    commitEdit();
  };
  const cancelEdit = () => {
    setEditName(item.name);
    setEditDescription(item.description ?? "");
    setIsEditing(false);
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `graphiql-collection-item-row${isDragOver ? " graphiql-collection-drop-target" : ""}`,
      "data-grabbed": isGrabbed || void 0,
      draggable: !readOnly && !isEditing,
      onDragStart: readOnly || isEditing ? void 0 : (e) => {
        e.dataTransfer.setData(
          "application/x-graphiql-item",
          JSON.stringify({ collectionId, index })
        );
        e.dataTransfer.effectAllowed = "move";
      },
      onDragOver: readOnly ? void 0 : (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setIsDragOver(true);
      },
      onDragLeave: readOnly ? void 0 : () => setIsDragOver(false),
      onDrop: readOnly ? void 0 : (e) => {
        e.preventDefault();
        setIsDragOver(false);
        try {
          const data = JSON.parse(
            e.dataTransfer.getData("application/x-graphiql-item")
          );
          onMove(data.collectionId, data.index, collectionId, index);
        } catch {
        }
      },
      onClick: () => {
        if (!isEditing) {
          onOpen(item);
        }
      },
      children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "graphiql-collection-drag-handle",
            "data-collection-drag-handle": item.id,
            "aria-label": `Reorder ${item.name}. Press space to grab, arrow keys to move, escape to cancel.`,
            "aria-pressed": isGrabbed,
            disabled: readOnly,
            onClick: (e) => e.stopPropagation(),
            onKeyDown: (e) => {
              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                onGrabToggle();
              } else if (isGrabbed && e.key === "ArrowDown") {
                e.preventDefault();
                e.stopPropagation();
                onGrabMove("down");
              } else if (isGrabbed && e.key === "ArrowUp") {
                e.preventDefault();
                e.stopPropagation();
                onGrabMove("up");
              } else if (isGrabbed && e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                onGrabCancel();
              }
            },
            children: "⠿"
          }
        ),
        method === "mix" ? /* @__PURE__ */ jsx(
          "span",
          {
            className: "graphiql-method-pill graphiql-collection-method-pill-mix",
            "aria-hidden": true,
            children: "MIX"
          }
        ) : /* @__PURE__ */ jsx(MethodPill, { operation: method, "aria-hidden": true }),
        isEditing ? /* @__PURE__ */ jsxs(
          "div",
          {
            ref: editFormRef,
            className: "graphiql-collection-item-edit-form",
            onClick: (e) => e.stopPropagation(),
            children: [
              /* @__PURE__ */ jsx(
                "input",
                {
                  className: "graphiql-collection-item-edit-input",
                  value: editName,
                  autoFocus: true,
                  placeholder: item.name,
                  onChange: (e) => setEditName(e.target.value),
                  onBlur: handleBlur,
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      commitEdit();
                    }
                    if (e.key === "Escape") {
                      cancelEdit();
                    }
                    e.stopPropagation();
                  }
                }
              ),
              /* @__PURE__ */ jsx(
                "input",
                {
                  className: "graphiql-collection-item-edit-input",
                  value: editDescription,
                  placeholder: "Description (optional)",
                  onChange: (e) => setEditDescription(e.target.value),
                  onBlur: handleBlur,
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      commitEdit();
                    }
                    if (e.key === "Escape") {
                      cancelEdit();
                    }
                    e.stopPropagation();
                  }
                }
              )
            ]
          }
        ) : /* @__PURE__ */ jsxs("div", { className: "graphiql-collection-item-main", children: [
          /* @__PURE__ */ jsx("span", { className: "graphiql-collection-item-name", title: item.name, children: item.name }),
          item.description && /* @__PURE__ */ jsx(
            "span",
            {
              className: "graphiql-collection-item-description",
              title: item.description,
              children: item.description
            }
          )
        ] }),
        !isEditing && /* @__PURE__ */ jsxs("div", { className: "graphiql-collection-item-actions", children: [
          !readOnly && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "graphiql-collection-item-action",
              "aria-label": `Rename ${item.name}`,
              title: "Rename",
              onClick: startEdit,
              children: /* @__PURE__ */ jsx(PenIcon, { "aria-hidden": "true" })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "graphiql-collection-item-action",
              "aria-label": `Copy operation ${item.name}`,
              title: "Copy operation",
              "data-confirmed": confirmed === "copy" || void 0,
              onClick: (e) => {
                e.stopPropagation();
                void runAction(
                  "copy",
                  () => navigator.clipboard.writeText(item.query),
                  "Copied query to clipboard.",
                  "Could not copy to clipboard."
                );
              },
              children: confirmed === "copy" ? /* @__PURE__ */ jsx(SvgCheck, { "aria-hidden": "true" }) : /* @__PURE__ */ jsx(CopyIcon, { "aria-hidden": "true" })
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "graphiql-collection-item-action",
              "aria-label": `Share ${item.name} as collection`,
              title: "Share as collection",
              "data-confirmed": confirmed === "share" || void 0,
              onClick: (e) => {
                e.stopPropagation();
                void runAction(
                  "share",
                  () => onShare(item.id),
                  "Shared operation to clipboard.",
                  "Could not share to clipboard."
                );
              },
              children: confirmed === "share" ? /* @__PURE__ */ jsx(SvgCheck, { "aria-hidden": "true" }) : /* @__PURE__ */ jsx(SvgUpload, { "aria-hidden": "true" })
            }
          ),
          !readOnly && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "graphiql-collection-item-action",
              "aria-label": `Delete ${item.name}`,
              title: "Delete",
              onClick: handleDelete,
              children: /* @__PURE__ */ jsx(TrashIcon, { "aria-hidden": "true" })
            }
          )
        ] })
      ]
    }
  );
};
export {
  CollectionItemRow
};
//# sourceMappingURL=collection-item-row.js.map
